import { useEffect } from "react";
import { transcribeIssueAudio } from "../api/transcribeIssueAudio";
import {
  trackTranscriptionFailed,
  trackTranscriptionRetryScheduled,
  trackTranscriptionSuccess
} from "../analytics/analyticsEvents";
import { getMediaBlob } from "../db/mediaRepo";
import {
  applyTranscriptionResult,
  forceQueueRetry,
  claimNextDueQueueJob,
  markQueueJobError,
  markQueueJobTranscribing,
  markQueueJobUploading,
  repairReportTranscriptionJobs,
  scheduleQueueRetry
} from "../db/reportRepo";
import { isOnline } from "./networkStatus";
import { isMobileBuild } from "../platform/runtime";
import { hasTranscriptionConsent } from "../app/transcriptionConsent";
import { APP_PAUSED_EVENT, APP_RESUMED_EVENT } from "../platform/lifecycle";

const QUEUE_EVENT = "moveinreport:transcription-queue";
const QUEUE_CHANNEL = "moveinreport-transcription-queue";
const WORKER_ID = createWorkerId();
let activeRun = false;
let broadcastChannel: BroadcastChannel | undefined;

export function notifyTranscriptionQueue(): void {
  window.dispatchEvent(new Event(QUEUE_EVENT));
  getBroadcastChannel()?.postMessage({ type: "queue-changed" });
}

export async function retryIssueTranscription(issueId: string): Promise<void> {
  await forceQueueRetry(issueId);
  notifyTranscriptionQueue();
}

export function useTranscriptionQueue(reportId: string | undefined, onChanged: () => void): void {
  useEffect(() => {
    let disposed = false;
    const run = () => {
      if (disposed) {
        return;
      }
      void processTranscriptionQueue(() => {
        onChanged();
        notifyTranscriptionQueue();
      }).then((changed) => {
        if (changed) {
          onChanged();
        }
      }).catch(() => { /* Leave durable jobs queued if local storage is temporarily unavailable. */ });
    };
    void repairReportTranscriptionJobs().then((changed) => {
      if (changed) {
        onChanged();
      }
      run();
    }).catch(() => { /* Retry on the next foreground/queue event. */ });
    const interval = window.setInterval(run, 15_000);
    const channel = getBroadcastChannel();
    const onBroadcast = () => run();
    channel?.addEventListener("message", onBroadcast);
    window.addEventListener("online", run);
    window.addEventListener(APP_RESUMED_EVENT, run);
    window.addEventListener(QUEUE_EVENT, run);
    document.addEventListener("visibilitychange", run);
    return () => {
      disposed = true;
      window.clearInterval(interval);
      channel?.removeEventListener("message", onBroadcast);
      window.removeEventListener("online", run);
      window.removeEventListener(APP_RESUMED_EVENT, run);
      window.removeEventListener(QUEUE_EVENT, run);
      document.removeEventListener("visibilitychange", run);
    };
  }, [onChanged, reportId]);
}

export async function processTranscriptionQueue(
  onChanged?: () => void
): Promise<boolean> {
  if (activeRun || !canProcessQueue()) {
    return false;
  }
  activeRun = true;
  let changed = false;
  try {
    while (canProcessQueue()) {
      if (isMobileBuild() && !(await hasTranscriptionConsent())) return changed;
      const job = await claimNextDueQueueJob(undefined, WORKER_ID);
      if (!job) {
        return changed;
      }
      const audioRecord = await getMediaBlob(job.audioBlobKey);
      if (!audioRecord) {
        await markQueueJobError(job.jobId, "No saved audio is available for transcription.");
        onChanged?.();
        changed = true;
        continue;
      }

      await markQueueJobUploading(job.jobId);
      onChanged?.();
      changed = true;
      const controller = new AbortController();
      const pause = () => controller.abort();
      const onHidden = () => { if (document.hidden) pause(); };
      window.addEventListener("offline", pause);
      window.addEventListener(APP_PAUSED_EVENT, pause);
      document.addEventListener("visibilitychange", onHidden);
      const requestStartedAt = Date.now();
      const queueAgeMs = Math.max(0, requestStartedAt - Date.parse(job.createdAt));
      try {
        const result = await transcribeIssueAudio(
          {
            audio: audioRecord.blob,
            issueId: job.issueId,
            clientDurationMs: job.audioDurationMs ?? 0,
            mimeType: job.audioMimeType || audioRecord.mimeType
          },
          controller.signal,
          (progress) => {
            if (progress.phase === "uploading") {
              void markQueueJobUploading(job.jobId, {
                loadedBytes: progress.loadedBytes,
                totalBytes: progress.totalBytes
              }).then(() => onChanged?.());
            } else {
              void markQueueJobTranscribing(job.jobId).then(() => onChanged?.());
            }
          }
        );
        await markQueueJobTranscribing(job.jobId);
        onChanged?.();
        await applyTranscriptionResult({
          jobId: job.jobId,
          issueId: job.issueId,
          title: result.title,
          transcript: result.transcript ?? result.description,
          suggestedDescription: result.suggestedDescription ?? result.description,
          description: result.description,
          modelUsed: result.modelUsed,
          needsManualEntry: result.needsManualEntry
        });
        trackTranscriptionSuccess({
          durationMs: job.audioDurationMs,
          recordingBytes: job.audioSizeBytes ?? audioRecord.sizeBytes,
          queueAgeMs,
          elapsedMs: Date.now() - requestStartedAt,
          modelUsed: result.modelUsed
        });
        onChanged?.();
      } catch (error) {
        trackTranscriptionFailed(error, {
          queueAgeMs,
          elapsedMs: Date.now() - requestStartedAt
        });
        const message =
          error instanceof DOMException && error.name === "AbortError"
            ? "Transcription paused. Your recording is saved and will retry."
            : error instanceof Error
              ? error.message
              : "Transcription failed. Please try again.";
        const attempts = job.attempts + 1;
        await scheduleQueueRetry(job.jobId, message, attempts);
        trackTranscriptionRetryScheduled({ queueAgeMs, safeErrorCode: "RETRY_SCHEDULED" });
        onChanged?.();
      } finally {
        window.removeEventListener("offline", pause);
        window.removeEventListener(APP_PAUSED_EVENT, pause);
        document.removeEventListener("visibilitychange", onHidden);
      }
    }
    return changed;
  } finally {
    activeRun = false;
  }
}

function getBroadcastChannel(): BroadcastChannel | undefined {
  if (typeof BroadcastChannel === "undefined") {
    return undefined;
  }
  broadcastChannel ??= new BroadcastChannel(QUEUE_CHANNEL);
  return broadcastChannel;
}

function createWorkerId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `queue_worker_${crypto.randomUUID()}`;
  }
  return `queue_worker_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
}

function canProcessQueue(): boolean {
  return isOnline() && document.visibilityState !== "hidden";
}
